import React, { useState } from 'react';
import { Stethoscope, Sparkles, Shield, Clock, ChevronDown, ChevronUp, Search, Syringe, User } from 'lucide-react';
import { services, detailedServices, contactInfo } from '../data/mock';

const iconComponents = {
  Stethoscope,
  Sparkles,
  Shield,
  Clock
};

const ServicesSection = () => {
  const accentClasses = ['accent-purple', 'accent-blue', 'accent-orange', 'accent-pink'];
  const [expandedService, setExpandedService] = useState(null);
  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(contactInfo.whatsappMessage)}`;

  const toggleExpanded = (serviceKey) => {
    setExpandedService(expandedService === serviceKey ? null : serviceKey);
  };

  return (
    <section id="services" className="py-24 bg-section">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="heading-1 mb-4">Servicios Dermatológicos Integrales</h2>
          <p className="body-large text-secondary max-w-2xl mx-auto">
            Desde tratamientos médicos hasta procedimientos estéticos, brindamos 
            soluciones completas para el cuidado de la piel adaptadas a tus necesidades únicas.
          </p>
        </div>

        {/* Services Grid */}
        <div className="voice-grid">
          {services.map((service, index) => {
            const IconComponent = iconComponents[service.icon];
            const accentClass = accentClasses[index % accentClasses.length];
            
            return (
              <div key={service.id} className={`voice-card ${accentClass} hover-lift`}>
                {/* Service Icon */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center">
                    <IconComponent size={24} className="text-primary" />
                  </div>
                  <h3 className="voice-card-title">{service.title}</h3>
                </div>

                {/* Service Description */}
                <p className="voice-card-description mb-6">
                  {service.description}
                </p>

                {/* Treatment List */}
                <div className="space-y-2 mb-6">
                  {service.treatments.map((treatment, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/60"></div>
                      <span className="body-small">{treatment}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="voice-card accent-grey-200 inline-block p-8 max-w-md">
            <h3 className="heading-3 mb-3">¿No estás seguro de qué tratamiento necesitas?</h3>
            <p className="body-small text-secondary mb-4">
              Agenda una consulta y nuestro equipo experto creará un plan de tratamiento personalizado para ti.
            </p>
            <a href={whatsappUrl} className="btn-primary inline-block">
              Agendar Consulta
            </a>
          </div>
        </div>

        {/* Detailed Services Sections */}
        <div className="mt-24 space-y-12">
          {/* Enfermedades Tratadas */}
          <div className="voice-card accent-purple">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                  <Search size={24} className="text-primary" />
                </div>
                <h3 className="heading-2">Enfermedades que Trato</h3>
              </div>
              <button 
                onClick={() => toggleExpanded('enfermedades')}
                className="btn-nav"
                aria-label="Expandir lista"
              >
                {expandedService === 'enfermedades' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            <p className="body-medium text-secondary mb-6">
              Diagnóstico y tratamiento especializado de diversas condiciones dermatológicas
            </p>
            
            {expandedService === 'enfermedades' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {detailedServices.enfermedadesTratadas.map((condition, index) => (
                  <div key={index} className="bg-white/30 rounded-lg p-3 text-center">
                    <span className="body-small font-medium">{condition}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Medicina Estética */}
          <div className="voice-card accent-blue">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                  <Syringe size={24} className="text-primary" />
                </div>
                <h3 className="heading-2">Servicios de Medicina Estética</h3>
              </div>
              <button 
                onClick={() => toggleExpanded('estetica')}
                className="btn-nav"
                aria-label="Expandir lista"
              >
                {expandedService === 'estetica' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            <p className="body-medium text-secondary mb-6">
              Tratamientos avanzados para rejuvenecimiento y embellecimiento facial y corporal
            </p>
            
            {expandedService === 'estetica' && (
              <div className="space-y-3">
                {detailedServices.medicinaEstetica.map((treatment, index) => (
                  <div key={index} className="bg-white/30 rounded-lg p-4">
                    <span className="body-medium font-medium">{treatment}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Medicina Quirúrgica */}
          <div className="voice-card accent-orange">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-primary" />
                </div>
                <h3 className="heading-2">Servicios de Medicina Quirúrgica</h3>
              </div>
              <button 
                onClick={() => toggleExpanded('quirurgica')}
                className="btn-nav"
                aria-label="Expandir lista"
              >
                {expandedService === 'quirurgica' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
            <p className="body-medium text-secondary mb-6">
              Procedimientos quirúrgicos especializados con técnicas de vanguardia
            </p>
            
            {expandedService === 'quirurgica' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {detailedServices.medicinaQuirurgica.map((procedure, index) => (
                  <div key={index} className="bg-white/30 rounded-lg p-4 text-center">
                    <span className="body-medium font-medium">{procedure}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;